/** @jsx jsx */
import React, { useEffect } from 'react';
import { css, styled, jsx } from '@compiled/react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import DynamicTable from '@atlaskit/dynamic-table';
import type { RowType } from '@atlaskit/dynamic-table/types';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { main_getArchivedIssuesForProjectQuery as FetchArchivedIssues } from '@atlassian/jira-relay/src/__generated__/main_getArchivedIssuesForProjectQuery.graphql';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { ARCHIVES_TABLE_TEST_ID, NUMBER_OF_ISSUES_PER_PAGE } from '../../common/ui/constants.tsx';
import { useArchiveIssueActions } from '../../controllers/main.tsx';
import {
	getCurrentPage,
	getLoadingState,
	useArchiveIssueStates,
	isIssueSelected,
} from '../../controllers/selectors.tsx';
import type { IssueDetailsType } from '../../controllers/types.tsx';
import { ArchivesTableEmptyState } from './empty-state/main.tsx';
import { FooterRenderer } from './footer-renderer/main.tsx';
import useTableHeadCells from './head/main.tsx';
import messages from './messages.tsx';
import DateCell from './rows/date-cell/main.tsx';
import { KeyCell } from './rows/key-cell/main.tsx';
import { SelectCell } from './rows/select-cell/main.tsx';
import { TextCell } from './rows/text-cell/main.tsx';
import { TypeCell } from './rows/type-cell/main.tsx';
import { UserCell } from './rows/user-cell/main.tsx';

export const ArchivesTable = ({
	queryReference,
}: {
	queryReference: PreloadedQuery<FetchArchivedIssues>;
}) => {
	const { formatMessage } = useIntl();
	const state = useArchiveIssueStates();
	const { setIssuesInCurrentPage, setArchivesIssueDataPresent, updateIssueDetails } =
		useArchiveIssueActions();
	const isLoading = getLoadingState({ state });
	const currentPage = getCurrentPage({ state });
	const data = usePreloadedQuery<FetchArchivedIssues>(
		graphql`
			query main_getArchivedIssuesForProjectQuery(
				$projectKey: String!
				$cloudId: ID!
				$selectedIssueTypes: [JiraIssueTypeInput!]
				$selectedReporters: [ID!]
				$archivalDateRange: JiraArchivedOnDateRange
				$selectedArchivedBy: [ID!]
				$searchBy: String
				$first: Int
				$last: Int
			) {
				jira {
					getArchivedIssuesForProject(
						searchBy: $searchBy
						filterBy: {
							byProject: $projectKey
							byIssueType: $selectedIssueTypes
							byReporter: $selectedReporters
							byArchivalDateRange: $archivalDateRange
							byArchivedBy: $selectedArchivedBy
						}
						first: $first
						last: $last
						cloudId: $cloudId
					) @optIn(to: "JiraGetArchivedIssuesForProjectQuery") {
						totalCount
						edges {
							node {
								issueId
								key
								issueTypeField {
									issueType {
										name
										avatar {
											medium
										}
										hierarchy {
											level
										}
									}
								}
								archivedBy {
									id
									picture
									name
								}
								fieldsById(ids: ["reporter"]) {
									edges {
										node {
											... on JiraSingleSelectUserPickerField {
												user {
													id
													name
													picture
												}
											}
										}
									}
								}
								archivedOn
								summaryField {
									text
								}
							}
						}
					}
					unarchiveIssuePermission: hasProjectPermission(
						cloudId: $cloudId
						permission: UNARCHIVE_ISSUES
						projectKey: $projectKey
					) @optIn(to: "JiraHasProjectPermissionQuery")
				}
			}
		`,
		queryReference,
	);

	const unarchiveIssuePermission = data?.jira?.unarchiveIssuePermission || false;
	const head = useTableHeadCells(unarchiveIssuePermission);
	const rows: RowType[] =
		data?.jira?.getArchivedIssuesForProject?.edges?.map((issueNode) => {
			const issue = issueNode?.node;
			const issueId = issue?.issueId?.toString() ?? '';
			const reporter = issue?.fieldsById?.edges?.[0]?.node?.user;
			const shouldHighlightRow = isIssueSelected({ state, pageNumber: currentPage, issueId });
			const issueTypeName = issue?.issueTypeField?.issueType?.name ?? '';
			const hierarchyLevel = issue?.issueTypeField?.issueType?.hierarchy?.level ?? 0;
			const selectCell =
				(unarchiveIssuePermission && [
					{
						key: `select-${issueId}`,
						content: (
							<SelectCell
								isChecked={shouldHighlightRow}
								isDisabled={hierarchyLevel === -1}
								issueId={issueId ?? ''}
								pageNumber={currentPage}
								issueKey={issue?.key ?? ''}
							/>
						),
					},
				]) ||
				[];
			return {
				key: issueId,
				cells: [
					...selectCell,
					{
						key: `type-${issueId}`,
						content: (
							<TypeCell
								iconUrl={issue?.issueTypeField?.issueType?.avatar?.medium ?? ''}
								name={issueTypeName}
							/>
						),
					},
					{
						key: `key-${issueId}`,
						content: <KeyCell issueKey={issue?.key ?? ''} />,
					},
					{
						key: `summary-${issueId}`,
						content: <TextCell text={issue?.summaryField?.text ?? ''} />,
					},
					{
						key: `reporter-${issueId}`,
						content: (
							<UserCell
								user={{
									id: reporter?.id || '',
									name: reporter?.name || '',
									avatarUrl: reporter?.picture || '',
								}}
							/>
						),
					},
					{
						key: `archivedDate-${issueId}`,
						content: <DateCell archivedDate={issue?.archivedOn ?? ''} />,
					},
					{
						key: `archviedBy-${issueId}`,
						content: (
							<UserCell
								user={{
									id: issue?.archivedBy?.id || '',
									name: issue?.archivedBy?.name || '',
									avatarUrl: issue?.archivedBy?.picture || '',
								}}
							/>
						),
					},
				],
				testId: `issue-archival-archives.archives-table.row-${issueId}`,
			};
		}) || [];

	useEffect(() => {
		// Update store with fetched issues data:
		const issuesInCurrentPage: string[] = [];
		const issuesDetails: IssueDetailsType = {};
		data?.jira?.getArchivedIssuesForProject?.edges?.forEach((issueNode) => {
			const issue = issueNode?.node;
			const issueId = issueNode?.node?.issueId?.toString() ?? '';
			const issueKey = issue?.key?.toString() ?? '';
			const hierarchyLevel = issue?.issueTypeField?.issueType?.hierarchy?.level ?? 0;

			if (hierarchyLevel !== -1) {
				issuesInCurrentPage.push(issueId);
			}
			issuesDetails[issueId] = {
				issueId,
				issueKey,
				hierarchyLevel,
			};
		});
		if (issuesInCurrentPage.length > 0) {
			setIssuesInCurrentPage(issuesInCurrentPage);
			updateIssueDetails(issuesDetails);
		}
	}, [setIssuesInCurrentPage, updateIssueDetails, data]);

	useEffect(() => {
		if (
			!state.refetchArchivesIssuesData &&
			rows?.length === 0 &&
			Object.keys(state.filters).length === 0 &&
			state.searchBy === ''
		) {
			setArchivesIssueDataPresent(false);
		}
	}, [
		rows?.length,
		setArchivesIssueDataPresent,
		state.filters,
		state.refetchArchivesIssuesData,
		state.searchBy,
	]);

	const totalIssueCount = data?.jira?.getArchivedIssuesForProject?.totalCount ?? 0;
	const firstIssuePosition = totalIssueCount
		? 1 + NUMBER_OF_ISSUES_PER_PAGE * (currentPage - 1)
		: totalIssueCount;
	const lastIssuePosition =
		NUMBER_OF_ISSUES_PER_PAGE * currentPage > totalIssueCount
			? totalIssueCount
			: NUMBER_OF_ISSUES_PER_PAGE * currentPage;

	const countRendererData = {
		issueNavigatorPageInfo: {
			firstIssuePosition,
			lastIssuePosition,
		},
		totalIssueSearchResultCount: totalIssueCount,
	};
	const totalPages = Math.ceil(totalIssueCount / NUMBER_OF_ISSUES_PER_PAGE);
	return (
		<UFOSegment name="issue-view-archived-issues-table">
			<JSErrorBoundary
				id="issue.issue-view.activity.archived-issues-table"
				packageName="jiraArchivedIssues"
				teamName="kenji"
				fallback="unmount"
			>
				<BorderWrapper>
					<div css={overflowStyles}>
						<Box xcss={wrapperStyles}>
							<DynamicTable
								isLoading={isLoading}
								head={head}
								rows={rows}
								isFixedSize
								label={formatMessage(messages.archivesTableLabel)}
								loadingLabel={formatMessage(messages.loadingArchivesTableLabel)}
								testId={ARCHIVES_TABLE_TEST_ID}
							/>
						</Box>
						{!rows || rows.length === 0 ? <ArchivesTableEmptyState /> : null}
					</div>
					<FooterRenderer
						currentPage={currentPage}
						totalPages={totalPages}
						countRendererData={countRendererData}
					/>
				</BorderWrapper>
			</JSErrorBoundary>
		</UFOSegment>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const BorderWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	table: {
		borderCollapse: 'collapse',
		borderBottom: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	tbody: {
		borderBottom: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'td, th': {
		borderWidth: '0.5px',
		borderStyle: 'solid',
		borderColor: `${token('color.border')}`,
		borderCollapse: 'collapse',
		verticalAlign: 'middle',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	th: {
		font: token('font.body'),
		fontWeight: token('font.weight.regular'),
		fontColor: token('color.text.subtlest'),
		paddingTop: token('space.075'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		borderTop: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'thead tr': {
		position: 'sticky',
		top: 0,
		// boxShadow: token(
		// 	'elevation.shadow.overflow',
		// 	'0px 0px 8px rgba(9, 30, 66, 0.16),0px 0px 1px rgba(9, 30, 66, 0.12)',
		// ),

		backgroundColor: token('elevation.surface'),
		zIndex: 1,
	},
	// First row first header cell
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'table tr:first-of-type th:first-of-type': {
		borderTopLeftRadius: token('space.100'),
	},
	// Left most cell in the table all rows
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'table th:first-of-type, table td:first-of-type': {
		paddingLeft: token('space.100'),
		borderLeft: 'none',
	},
	// First row last header cell
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'table tr:first-of-type th:last-of-type': {
		borderTopRightRadius: token('space.100'),
	},
	// Right most cell in the table all rows
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'table th:last-of-type, table td:last-of-type': {
		paddingRight: token('space.100'),
		borderRight: 'none',
	},
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
});

const overflowStyles = css({
	overflowX: 'auto',
	overflowY: 'auto',
	maxHeight: '75vh',
	borderWidth: '1px',
	borderStyle: 'solid',
	borderColor: `${token('color.border')}`,
	borderTopLeftRadius: token('space.100'),
	borderTopRightRadius: token('space.100'),
	flex: 1,
});

const wrapperStyles = xcss({
	maxHeight: '75vh',
	width: '100%',
	minWidth: '1120px',
	marginBottom: 'space.negative.300',
});
